<template>
  <WdsStack direction-column gap-md>
    <a :href="linkUrl" data-test="job-form-header-go-back-link">
      <WdsButtonGhost secondary>
        <WdsStack align-center gap-sm>
          <WdsIcon name="arrow-left" :size="16" />
          {{ linkText }}
        </WdsStack>
      </WdsButtonGhost>
    </a>

    <WdsStack direction-row justify-space-between gap-md>
      <WdsStack direction-column gap-md>
        <WdsStack direction-row align-start gap-md>
          <WdsHeading as="h1" type="display">{{ title }}</WdsHeading>

          <WdsLabel v-if="showBetaLabel" xs warning subtle>
            <WdsStack direction-row gap-xs align-center>
              Beta
              <WdsIcon
                :size="14"
                weight="thin"
                :name="WdsIconNameEnum.LIGHT"
                class="!tw-inline-block"
              />
            </WdsStack>
          </WdsLabel>

          <WdsTooltip
            v-if="isBrief"
            :text="$t('This job is intended for a pre-identified candidate.')"
          >
            <WdsLabel md warning subtle>{{ $t('Job brief') }}</WdsLabel>
          </WdsTooltip>
        </WdsStack>

        <WdsStack direction-column justify-space-between gap-sm>
          <WdsText v-if="subtitle" :text="subtitle" primary xl />

          <WdsStack direction-row align-center gap-sm>
            <WdsText v-if="jobLocalId" tertiary>{{ $t('Job ID:') }} #{{ jobLocalId }}</WdsText>

            <JobHeaderStatus
              :removed="removed"
              :paused="paused"
              :published="published"
              :available="available"
              :completed="completed"
            />
          </WdsStack>
          <WdsStack v-if="showOwners" direction-row align-center gap-sm>
            <WdsText v-if="isOwner" tertiary>{{ $t('Job owners:') }} {{ jobOwnersNames }}</WdsText>
            <a :href="ownerEditLink"><WdsIcon name="edit" :size="14" /></a>
          </WdsStack>
          <CustomFieldsTagsList v-if="hasCustomFields" :tags="customFieldsTagsList" />
        </WdsStack>
      </WdsStack>

      <slot name="right">
        <WdsStack direction-row justify-space-between align-end gap-sm>
          <WdsText>
            {{ $t('Need help?') }}
            <a href="#" class="intercomchat">{{ $t('Chat with us') }}.</a>
          </WdsText>
        </WdsStack>
      </slot>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsHeading,
  WdsButtonGhost,
  WdsIcon,
  WdsText,
  WdsLabel,
  WdsTooltip,
  WdsIconNameEnum,
} from '@worksome/wds-components'

import { $t } from '@/composables/translation'
import JobHeaderStatus from '@/components/Job/HeaderStatus.vue'
import CustomFieldsTagsList from '@/components/Fields/CustomFieldsTagsList.vue'

defineProps({
  linkText: {
    type: String,
    required: true,
  },

  linkUrl: {
    type: String,
    required: true,
  },

  jobLocalId: {
    type: Number,
    required: false,
    default: null,
  },

  title: {
    type: String,
    required: true,
  },

  subtitle: {
    type: String,
    required: false,
    default: null,
  },

  status: {
    type: String,
    required: false,
    default: null,
  },

  showHelpLink: {
    type: Boolean,
    required: false,
    default: false,
  },

  removed: {
    type: Boolean,
    default: false,
  },

  paused: {
    type: Boolean,
    default: false,
  },

  published: {
    type: Boolean,
    default: false,
  },

  available: {
    type: Boolean,
    default: false,
  },

  completed: {
    type: Boolean,
    default: false,
  },

  hasOwners: {
    type: Boolean,
    default: false,
  },

  isOwner: {
    type: Boolean,
    default: false,
  },

  showOwners: {
    type: Boolean,
    default: false,
  },

  jobOwnersNames: {
    type: String,
    default: '',
  },

  ownerEditLink: {
    type: String,
    default: '',
  },

  hasCustomFields: {
    type: Boolean,
    default: false,
  },

  customFieldsTagsList: {
    type: Array as () => { label: string; primary: boolean }[],
    default: [],
  },

  showBetaLabel: {
    type: Boolean,
    default: false,
  },

  isBrief: {
    type: Boolean,
    default: false,
  },
})
</script>
